import { useEffect, useRef } from 'react';

/**
 * Custom hook to handle data fetching when filter parameters change.
 *
 * This hook tracks changes in `event_id` and `metric_id` within `filterParams`
 * and triggers the appropriate data fetch functions.
 *
 * - If `event_id` changes, it calls `fetchData(true)`.
 * - If `metric_id` changes:
 *   - If it has a valid value, it calls `fetchDataWithFilters(metric_id)`.
 *   - If it is `undefined`, it calls `fetchData()`.
 *
 * @param {Object} filterParams - Object containing the search filters.
 * @param {string | number | undefined} filterParams.event_id - The current event ID.
 * @param {string | number | undefined} filterParams.metric_id - The selected metric ID.
 * @param {Function} fetchData - Function to fetch general data.
 * @param {Function} fetchDataWithFilters - Function to fetch data based on the selected metric.
 */
const useFetchDataOnFilterChange = (filterParams, fetchData, fetchDataWithFilters) => {
  const prevFilters = useRef({
    event_id: undefined,
    metric_id: undefined,
    show_transit: undefined,
    start_date: undefined,
    end_date: undefined,
  });

  /**
   * Checks if a filter has changed and triggers the appropriate fetch function.
   * @param {string} key - The filter key (e.g., "event_id" or "metric_id").
   * @param {any} newValue - The new value of the filter.
   * @param {Function} fetchFn - The function to call when the filter changes.
   */
  const updateFilterAndFetch = (key, newValue, fetchFn) => {
    if (prevFilters.current[key] !== newValue) {
      prevFilters.current[key] = newValue;
      fetchFn();
    }
  };

  useEffect(() => {
    const end_date = filterParams.end_date;

    updateFilterAndFetch('event_id', filterParams.event_id, () => fetchData(true));
    updateFilterAndFetch('show_transit', filterParams.show_transit, () => fetchData(true));
    updateFilterAndFetch('start_date', filterParams.start_date, () => fetchData(true));
    updateFilterAndFetch('end_date', end_date, () => fetchData(true));
    updateFilterAndFetch('metric_id', filterParams.metric_id, () => {
      filterParams.metric_id !== undefined
        ? fetchDataWithFilters(filterParams.metric_id)
        : fetchData();
    });
  }, [
    filterParams.event_id,
    filterParams.metric_id,
    filterParams.show_transit,
    filterParams.start_date,
    filterParams.end_date,
    fetchData,
    fetchDataWithFilters,
  ]);
};

export default useFetchDataOnFilterChange;
